import { useLocation, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Base } from '@itsrever/design-system'
import { LoadError, PageHeader } from '@/ui/shared'
import { useWarehousesScope } from '@/hooks/use-warehouses-scope'
import { integrationKindOf, isBroken } from '@/domain/nango-connection'
import { useGroupNangoConnections } from '@/application/manage-nango-connections'
import { useGroupWarehouses } from '@/application/manage-warehouses'
import { useActiveIntegrations } from '@/application/queries/use-integrations'
import { HUB_PATHS, HubSection } from './hub-paths'
import { CARRIERS } from './carriers-section'
import { IntegrationsBanner } from './integrations-banner'
import { NeedsAttention } from './needs-attention'
import {
  ProviderLogo,
  providerName,
  providerOf,
  RAW_TEXT,
  ViewOnlyNotice
} from './hub-parts'

const { Button, Card } = Base

// One retry, not React Query's three: the overview would otherwise show "Loading…" for ~7s.
const OVERVIEW_RETRY = 1

interface OverviewCard {
  section: HubSection
  title: string
  summary: string
  logos: { provider: string; name: string; src?: string }[]
}

export default function IntegrationsOverview({
  readOnly = false
}: {
  readOnly?: boolean
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { search } = useLocation()
  const { scope } = useWarehousesScope()
  const group = scope.kind === 'none' ? undefined : scope.group
  const groupStores = scope.kind === 'none' ? [] : scope.stores
  const {
    connections,
    loadingConnections,
    connectionsError,
    reloadConnections
  } = useGroupNangoConnections({
    enabled: !!group,
    group,
    retry: OVERVIEW_RETRY
  })
  const { warehouses, loadingWarehouses, warehousesError, reloadWarehouses } =
    useGroupWarehouses(group, {
      retry: OVERVIEW_RETRY
    })
  const carriers = useActiveIntegrations(groupStores, CARRIERS)
  const failed = connectionsError || (!!group && warehousesError)
  const retryFailed = () => {
    if (connectionsError) reloadConnections()
    if (warehousesError) reloadWarehouses()
  }

  const ofKind = (kind: 'platform' | 'app') =>
    connections.filter((c) => integrationKindOf(c) === kind)
  // One logo per integration: a group with three Shopify stores shows Shopify once.
  const logosOf = (kind: 'platform' | 'app') => [
    ...new Map(
      ofKind(kind).map((c) => [
        providerOf(c),
        { provider: providerOf(c), name: providerName(c) }
      ])
    ).values()
  ]
  const connectionsSummary = (kind: 'platform' | 'app') =>
    connectionsError
      ? t('integrations_hub.summary_error')
      : loadingConnections
        ? t('integrations_hub.summary_loading')
        : t(`integrations_hub.${kind}.summary`, {
            count: ofKind(kind).length
          })

  const allCards: OverviewCard[] = [
    {
      section: 'platform',
      title: t('integrations_hub.platform.title'),
      summary: connectionsSummary('platform'),
      logos: logosOf('platform')
    },
    {
      section: 'warehouses',
      title: t('integrations_hub.warehouses.title'),
      summary: warehousesError
        ? t('integrations_hub.summary_error')
        : loadingWarehouses
          ? t('integrations_hub.summary_loading')
          : t('integrations_hub.warehouses.summary', {
              count: warehouses.length
            }),
      logos: []
    },
    {
      section: 'carriers',
      title: t('integrations_hub.carriers.title'),
      summary: t('integrations_hub.carriers.summary'),
      logos: carriers.map((carrier) => ({
        provider: carrier.integration_name ?? '',
        name: (carrier.integration_name ?? '').replace(/_/g, ' '),
        src: carrier.integration_logo
      }))
    },
    {
      section: 'apps',
      title: t('integrations_hub.app.title'),
      summary: connectionsSummary('app'),
      logos: logosOf('app')
    }
  ]
  // Without a group there are no group-level warehouses to show.
  const cards = allCards.filter(
    (card) => card.section !== 'warehouses' || !!group
  )

  return (
    <>
      <PageHeader
        title={t('integrations_hub.title')}
        description={
          group
            ? t('integrations_hub.description', { group, ...RAW_TEXT })
            : t('integrations_hub.no_scope')
        }
      />
      <div className="flex max-w-screen-xl flex-col gap-4 px-7 py-4">
        {readOnly ? <ViewOnlyNotice /> : null}
        <IntegrationsBanner />
        {group ? (
          <NeedsAttention
            group={group}
            groupStores={groupStores}
            broken={connections.filter(isBroken)}
            onReconnected={() => reloadConnections()}
            readOnly={readOnly}
          />
        ) : null}
        {group && failed ? (
          <LoadError
            message={t('integrations_hub.overview_load_error')}
            onRetry={retryFailed}
          />
        ) : null}
        <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-4">
          {cards.map((card) => (
            <Card key={card.section} className="flex flex-col gap-3 p-5">
              <div>
                <p className="text-sm font-medium text-neutral-900">
                  {card.title}
                </p>
                {group ? (
                  <p className="text-sm text-neutral-600">{card.summary}</p>
                ) : null}
              </div>
              <div className="flex min-h-7 items-center gap-1.5">
                {card.logos.map((logo) => (
                  <ProviderLogo key={logo.provider} {...logo} />
                ))}
              </div>
              <div className="mt-auto">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  aria-label={t('integrations_hub.manage_aria', {
                    title: card.title,
                    ...RAW_TEXT
                  })}
                  onClick={() =>
                    navigate(`${HUB_PATHS[card.section]}${search}`)
                  }
                >
                  {t('integrations_hub.manage')}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </>
  )
}
